/**
 * 原生购物车抽屉：负责开关、焦点管理、Section Rendering 和购物车行项目更新。
 *
 * 依赖：assets/cart.js 暴露的 window.CartApi。
 * 服务端视图：sections/cart-drawer.liquid、sections/cart-icon-bubble.liquid。
 */
(function () {
  "use strict";

  if (customElements.get("cart-drawer")) return;

  const DRAWER_SECTION = "cart-drawer";
  const BUBBLE_SECTION = "cart-icon-bubble";
  const SECTIONS = [DRAWER_SECTION, BUBBLE_SECTION];
  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "textarea:not([disabled])",
    "select:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  class CartDrawerElement extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.mutationPending = false;
      this.noteTimer = null;
      this.openedBy = null;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.onClick);
      this.addEventListener("change", this.onChange);
      this.addEventListener("input", this.onInput);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      this.removeEventListener("change", this.onChange);
      this.removeEventListener("input", this.onInput);
      document.removeEventListener("keydown", this.onKeydown);
      clearTimeout(this.noteTimer);
    }

    open(source = null) {
      if (this.isOpen) return;

      this.isOpen = true;
      this.openedBy =
        source instanceof HTMLElement
          ? source
          : document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;

      this.setAttribute("aria-hidden", "false");
      document.body.classList.add("cart-drawer-v2-open");
      document.addEventListener("keydown", this.onKeydown);
      this.openedBy?.setAttribute("aria-expanded", "true");

      requestAnimationFrame(() => {
        this.classList.add("is-active");
        const target =
          this.querySelector("[data-cart-drawer-close]") ||
          this.querySelector("[role='dialog']");
        target?.focus({ preventScroll: true });
      });

      document.dispatchEvent(
        new CustomEvent("cart-drawer:open", { detail: { drawer: this } }),
      );
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.classList.remove("is-active");
      this.setAttribute("aria-hidden", "true");
      document.body.classList.remove("cart-drawer-v2-open");
      document.removeEventListener("keydown", this.onKeydown);
      this.openedBy?.setAttribute("aria-expanded", "false");
      this.openedBy?.focus({ preventScroll: true });
      this.openedBy = null;
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        this.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((element) => element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        this.querySelector("[role='dialog']")?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (
        target.closest("[data-cart-drawer-close]") ||
        target.closest("[data-cart-drawer-overlay]")
      ) {
        event.preventDefault();
        this.close();
        return;
      }

      const remove = target.closest("[data-cart-remove]");
      if (remove) {
        event.preventDefault();
        this.updateLine(remove.dataset.lineKey, 0);
        return;
      }

      const quantityButton = target.closest(
        "[data-cart-quantity-minus], [data-cart-quantity-plus]",
      );
      if (!quantityButton) return;

      event.preventDefault();
      const item = quantityButton.closest("[data-cart-drawer-item]");
      const input = item?.querySelector("[data-cart-quantity-input]");
      if (!input) return;

      const current = Number(input.value) || 0;
      const step = Math.max(1, Number(input.step) || 1);
      const minimum = Math.max(0, Number(input.dataset.min) || 1);
      const maximum = input.max ? Number(input.max) : Infinity;
      const isMinus = quantityButton.matches("[data-cart-quantity-minus]");
      let next = isMinus ? current - step : current + step;

      if (isMinus && next < minimum) next = 0;
      if (!isMinus) next = Math.min(next, maximum);

      this.updateLine(input.dataset.lineKey, next);
    }

    onChange(event) {
      const input = event.target.closest?.("[data-cart-quantity-input]");
      if (!input) return;

      const minimum = Math.max(0, Number(input.dataset.min) || 1);
      const maximum = input.max ? Number(input.max) : Infinity;
      const step = Math.max(1, Number(input.step) || 1);
      let quantity = Number(input.value);

      if (!Number.isFinite(quantity) || quantity < 0) quantity = minimum;
      if (quantity > 0) {
        quantity = Math.max(minimum, quantity);
        quantity = minimum + Math.round((quantity - minimum) / step) * step;
      }
      quantity = Math.min(quantity, maximum);
      input.value = String(quantity);

      this.updateLine(input.dataset.lineKey, quantity);
    }

    onInput(event) {
      const note = event.target.closest?.("[data-cart-note]");
      if (!note) return;

      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => this.updateNote(note.value), 400);
    }

    async updateLine(lineKey, quantity) {
      if (!lineKey || this.mutationPending || !window.CartApi) return false;

      this.setLoading(true);
      this.clearErrors();

      try {
        const cart = await window.CartApi.change(lineKey, quantity, {
          sections: SECTIONS,
          sectionsUrl: `${window.location.pathname}${window.location.search}`,
        });
        this.renderContents(cart);
        return true;
      } catch (error) {
        this.showError(this.errorMessage(error), lineKey);
        return false;
      } finally {
        this.setLoading(false);
      }
    }

    async updateNote(note) {
      if (!window.CartApi) return false;

      try {
        const cart = await window.CartApi.update({ note });
        document.dispatchEvent(
          new CustomEvent("cart:updated", { detail: cart }),
        );
        return true;
      } catch (error) {
        this.showError(this.errorMessage(error));
        return false;
      }
    }

    async refresh() {
      if (this.mutationPending) return false;

      this.setLoading(true);
      this.clearErrors();

      try {
        // Section Rendering API：在当前页面 URL 上附加 sections 参数，局部拉取
        // cart-drawer / cart-icon-bubble 的服务端 HTML，避免整页刷新。
        const url = new URL(window.location.href);
        url.searchParams.set("sections", SECTIONS.join(","));
        // 商品路由会把 Accept: application/json 解释为 Product JSON 请求，
        // 导致忽略 sections 参数并返回 { product }。保持浏览器默认 Accept，
        // Shopify 才会在所有页面类型下返回 Section Rendering JSON。
        const response = await fetch(url.toString());

        if (!response.ok) throw new Error(`Cart sections: ${response.status}`);

        const sections = await response.json();
        this.renderContents({ sections });
        return true;
      } catch (error) {
        this.showError(this.errorMessage(error));
        return false;
      } finally {
        this.setLoading(false);
      }
    }

    renderContents(state) {
      const drawerHtml = state?.sections?.[DRAWER_SECTION];
      if (!drawerHtml) {
        throw new Error("Cart drawer section is unavailable");
      }

      const drawerDocument = new DOMParser().parseFromString(
        drawerHtml,
        "text/html",
      );
      const nextDrawer = drawerDocument.querySelector(
        "cart-drawer[data-cart-drawer-v2]",
      );
      const nextContainer = nextDrawer?.querySelector("#CartDrawer");
      const currentContainer = this.querySelector("#CartDrawer");

      if (!nextDrawer || !nextContainer || !currentContainer) {
        throw new Error("Cart drawer section markup is invalid");
      }

      currentContainer.replaceWith(nextContainer);
      this.classList.toggle("is-empty", nextDrawer.classList.contains("is-empty"));
      this.dataset.cartItemCount = nextDrawer.dataset.cartItemCount || "0";
      this.dataset.cartTotalPrice = nextDrawer.dataset.cartTotalPrice || "0";
      this.syncCartBubbles(state.sections[BUBBLE_SECTION]);

      const cart = {
        ...state,
        item_count: Number(this.dataset.cartItemCount) || 0,
        total_price: Number(this.dataset.cartTotalPrice) || 0,
      };

      document.dispatchEvent(
        new CustomEvent("cart:drawer:loaded", { detail: cart }),
      );
      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: cart }),
      );

      // DOM 被替换后焦点会丢失；收到 panel 上，避免落到数量 input/按钮
      if (this.isOpen) {
        this.querySelector("[role='dialog']")?.focus({ preventScroll: true });
      }
    }

    syncCartBubbles(html) {
      if (!html) return;

      const fragment = new DOMParser().parseFromString(html, "text/html");
      const nextBubble = fragment.querySelector(".cart-link__bubble");
      if (!nextBubble) return;

      document.querySelectorAll(".cart-link__bubble").forEach((bubble) => {
        bubble.replaceWith(nextBubble.cloneNode(true));
      });
    }

    setLoading(value) {
      this.mutationPending = value;
      this.toggleAttribute("loading", value);
      this.setAttribute("aria-busy", value ? "true" : "false");

      this.querySelectorAll(
        "[data-cart-quantity] button, [data-cart-quantity] input, [data-cart-remove]",
      ).forEach((control) => {
        if (value && !control.disabled) {
          control.disabled = true;
          control.dataset.cartDrawerPending = "true";
        } else if (!value && control.dataset.cartDrawerPending) {
          control.disabled = false;
          delete control.dataset.cartDrawerPending;
        }
      });

      this.querySelectorAll(
        "[data-cart-drawer-items], [data-cart-drawer-footer], [data-cart-drawer-empty]",
      ).forEach((region) => region.toggleAttribute("inert", value));
    }

    clearErrors() {
      this.querySelectorAll("[data-cart-drawer-errors], [data-cart-line-error]").forEach(
        (element) => {
          element.textContent = "";
        },
      );
    }

    showError(message, lineKey = "") {
      const item = lineKey
        ? Array.from(this.querySelectorAll("[data-cart-drawer-item]")).find(
            (candidate) => candidate.dataset.lineKey === lineKey,
          )
        : null;
      const region =
        item?.querySelector("[data-cart-line-error]") ||
        this.querySelector("[data-cart-drawer-errors]");

      if (region) region.textContent = message;
    }

    errorMessage(error) {
      return (
        error?.description ||
        error?.message ||
        window.theme?.strings?.cartError ||
        "Cart could not be updated. Please try again."
      );
    }
  }

  customElements.define("cart-drawer", CartDrawerElement);
})();
